import React from 'react';

import { Alert, Button, Grid } from '@mui/material';
import { FormApi } from 'final-form';

import { Field, Form, TextField } from '@hrlogics/react-components/form';

import { useChangeUserPasswordMutation } from 'src/api';
import { PasswordField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useUserPasswordChangeRequestIdParamId } from 'src/hooks/user';

import { ChangePasswordFormFields, ChangePasswordFormValues } from '../types';
import { changePasswordFormValidationSchema, mfaCodeFormValidationSchema } from '../utils';

interface ChangePasswordFormProps {
  switchClosability: (isClosureAllowed: boolean) => void;
  onSubmitCallback: () => void;
}

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  onSubmitCallback,
  switchClosability,
}) => {
  const userPasswordChangeRequestId = useUserPasswordChangeRequestIdParamId();

  const [mfaSession, setMfaSession] = React.useState<string | null>(null);

  const { spawnNotification } = useNotification();

  const [changeUserPasswordMutation] = useChangeUserPasswordMutation();

  const formValidationSchema = React.useMemo(() => {
    if (mfaSession) {
      return changePasswordFormValidationSchema.and(mfaCodeFormValidationSchema);
    }
    return changePasswordFormValidationSchema;
  }, [mfaSession]);

  const onSubmit = React.useCallback(
    async (formValues: ChangePasswordFormValues, form: FormApi<ChangePasswordFormValues>) => {
      if (!userPasswordChangeRequestId) return;

      switchClosability(false);
      try {
        const { data } = await changeUserPasswordMutation({
          variables: {
            currentPassword: formValues.currentPassword,
            newPassword: formValues.newPassword,
            userPasswordChangeRequestId,
            mfaSession,
            mfaCode: formValues.mfaCode,
          },
        });

        switchClosability(true);

        if (data?.changeUserPassword.mfaSession) {
          setMfaSession(data.changeUserPassword.mfaSession);
        }
        if (data?.changeUserPassword.success) {
          spawnNotification('Password changed successfully', 'success');
          form.restart();
          onSubmitCallback();
        }
      } catch (error) {
        console.error(error);
        switchClosability(true);
      }
    },
    [
      changeUserPasswordMutation,
      onSubmitCallback,
      userPasswordChangeRequestId,
      switchClosability,
      spawnNotification,
      mfaSession,
    ],
  );

  return (
    <Form
      onSubmit={onSubmit}
      validationSchema={formValidationSchema}
      subscription={{ dirty: true, submitting: true, valid: true }}
    >
      {({ form, dirty, submitting, valid }) => (
        <Grid container spacing={3}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Field
                name={ChangePasswordFormFields.currentPassword}
                fullWidth
                label="Current Password"
                required
                component={PasswordField}
              />
            </Grid>
            <Grid size={12}>
              <Field
                name={ChangePasswordFormFields.newPassword}
                fullWidth
                label="New Password"
                required
                component={PasswordField}
              />
            </Grid>
            <Grid size={12}>
              <Field
                name={ChangePasswordFormFields.confirmNewPassword}
                fullWidth
                label="Confirm New Password"
                required
                component={PasswordField}
              />
            </Grid>
            {mfaSession && (
              <React.Fragment>
                <Grid size={12}>
                  <Alert severity="warning">
                    MFA is enabled on your account. Please enter the code from your authenticator
                    app to complete the password change.
                  </Alert>
                </Grid>
                <Grid size={12}>
                  <Field
                    name={ChangePasswordFormFields.mfaCode}
                    fullWidth
                    label="MFA Code"
                    required
                    component={TextField}
                  />
                </Grid>
              </React.Fragment>
            )}
          </Grid>

          <Grid
            container
            spacing={3}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            size={12}
          >
            <Grid>
              <Button
                type="reset"
                color="error"
                disabled={!dirty || submitting}
                variant="outlined"
                onClick={() => form.restart()}
              >
                Cancel
              </Button>
            </Grid>
            <Grid>
              <Button
                disabled={!dirty || submitting || !valid}
                loading={submitting}
                variant="contained"
                type="submit"
              >
                Confirm
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Form>
  );
};
